import { useEffect } from 'react'

import {
  jingleCollectionByIdQuery,
  jingleCollectionCreate,
  jingleCollectionUpdate,
} from '@/entities/jingle-collections'
import {
  hasFormChanged,
  parseDateStringToDayjsDate,
} from '@/shared/lib/form-utils'
import { showNotificationWarningFx } from '@/shared/lib/providers'
import { HidingSpin, SelfValidateInput } from '@/shared/ui'
import { PrimaryButton } from '@/shared/ui'
import {
  Button,
  Checkbox,
  DatePicker,
  Flex,
  Form,
  InputNumber,
  Space,
  Typography,
} from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { useUnit } from 'effector-react'
import { useFormik } from 'formik'
import { z } from 'zod'
import { toFormikValidationSchema } from 'zod-formik-adapter'

import { MAX_FIELD_LENGTH } from '../lib/constants'
import { getJingleCollectionRequestByFormValues } from '../lib/get-jingle-collection-by-form'

const DEFAULT_VALUES = {
  name: '',
  frequency: 1,
  startDate: dayjs(),
  endDate: null,
  dateRangeSelection: false,
}

const jingleCollectionSchema = z.object({
  name: z.string().min(1),
  frequency: z.number().min(DEFAULT_VALUES.frequency),
  startDate: z.custom<Dayjs>(),
  endDate: z.custom<Dayjs>().nullable(),
  dateRangeSelection: z.boolean(),
})

export type JingleCollectionFormCommonInformationModel = z.infer<
  typeof jingleCollectionSchema
>

export const JingleCollectionFormCommonInformation = () => {
  // Form data
  const jingleCollection = useUnit(jingleCollectionByIdQuery)
  const jingleCollectionQueryStatus = useUnit(jingleCollectionByIdQuery.$status)

  const createJingleCollection = useUnit(jingleCollectionCreate)
  const updateJingleCollection = useUnit(jingleCollectionUpdate)

  const submitting =
    createJingleCollection.pending || updateJingleCollection.pending
  const formik = useFormik<JingleCollectionFormCommonInformationModel>({
    initialValues: jingleCollection.data
      ? {
          name: jingleCollection.data.name,
          frequency: jingleCollection.data.frequency,
          startDate: parseDateStringToDayjsDate(
            jingleCollection.data.startDate,
          ),
          endDate: jingleCollection.data.endDate
            ? parseDateStringToDayjsDate(jingleCollection.data.endDate)
            : null,
          dateRangeSelection: Boolean(jingleCollection.data.endDate),
        }
      : DEFAULT_VALUES,
    validationSchema: toFormikValidationSchema(jingleCollectionSchema),

    onSubmit: (
      values: JingleCollectionFormCommonInformationModel,
      { setSubmitting },
    ) => {
      if (!values.endDate && values.dateRangeSelection) {
        void showNotificationWarningFx({ message: 'Не выбрана дата окончания' })
        return
      }
      const jingleCollectionSubmittedData =
        getJingleCollectionRequestByFormValues(values)
      if (jingleCollection.data) {
        void updateJingleCollection.start({
          id: jingleCollection.data.id,
          data: jingleCollectionSubmittedData,
        })
      } else {
        void createJingleCollection.start({
          data: jingleCollectionSubmittedData,
        })
      }
      setSubmitting(false)
    },
  })

  const { isValid, isValidating, isSubmitting } = formik
  useEffect(() => {
    if (!isValid && !isValidating && isSubmitting) {
      void showNotificationWarningFx({ message: 'Не все поля заполнены' })
    }
  }, [isSubmitting, isValid, isValidating])

  const formControlButtonsDisabled =
    !hasFormChanged(formik.values, formik.initialValues) || submitting

  return (
    <>
      <HidingSpin
        spinning={
          jingleCollectionQueryStatus !== 'initial' &&
          (jingleCollection.pending ||
            (!jingleCollection.data && !jingleCollection.error))
        }
      >
        <div style={{ maxWidth: 600 }}>
          <Form.Item layout="vertical" label="Название">
            <SelfValidateInput
              showCount
              maxLength={MAX_FIELD_LENGTH.name}
              value={formik.values.name}
              onChange={(value) => {
                void formik.setFieldValue('name', value)
              }}
            />
          </Form.Item>

          <Form.Item layout="vertical" label="Частота воспроизведения">
            <Space>
              <Typography.Text type="secondary">Каждый(-е)</Typography.Text>
              <InputNumber
                min={DEFAULT_VALUES.frequency}
                defaultValue={DEFAULT_VALUES.frequency}
                value={formik.values.frequency}
                onChange={(value) => {
                  void formik.setFieldValue(
                    'frequency',
                    value || DEFAULT_VALUES.frequency,
                  )
                }}
              />
              <Typography.Text type="secondary">трек(-ов)</Typography.Text>
            </Space>
          </Form.Item>

          <Form.Item
            layout="vertical"
            label="Дата"
            style={{ width: 'fit-content' }}
          >
            <Flex vertical gap={8}>
              {formik.values.dateRangeSelection ? (
                <DatePicker.RangePicker
                  inputReadOnly
                  value={[formik.values.startDate, formik.values.endDate]}
                  onChange={(value) => {
                    if (value && value.length === 2) {
                      if (value[0])
                        void formik.setFieldValue('startDate', value[0])
                      if (value[1])
                        void formik.setFieldValue('endDate', value[1])
                    }
                  }}
                  minDate={DEFAULT_VALUES.startDate}
                />
              ) : (
                <DatePicker
                  inputReadOnly
                  value={formik.values.startDate}
                  onChange={(value) => {
                    void formik.setFieldValue('startDate', value)
                  }}
                  minDate={DEFAULT_VALUES.startDate}
                />
              )}

              <Checkbox
                checked={formik.values.dateRangeSelection}
                onChange={(e) =>
                  void formik.setFieldValue(
                    'dateRangeSelection',
                    e.target.checked,
                  )
                }
              >
                Указать период
              </Checkbox>
            </Flex>
          </Form.Item>

          <Space>
            {!jingleCollection.data && (
              <Button
                disabled={formControlButtonsDisabled}
                onClick={() => formik.resetForm()}
              >
                Очистить
              </Button>
            )}
            <PrimaryButton
              disabled={formControlButtonsDisabled}
              loading={submitting}
              onClick={() => {
                formik.handleSubmit()
              }}
            >
              {jingleCollection.data ? 'Сохранить' : 'Продолжить'}
            </PrimaryButton>
          </Space>
        </div>
      </HidingSpin>
    </>
  )
}
